<template>
  <v-card :disabled="lockSettings">
    <v-card-title class="emergency-title pb-0 mb-4">
      <v-icon color="red" v-show="lockSettings">mdi-lock</v-icon>
      {{ $t('settings.emergency.title') }}
    </v-card-title>
    <v-card-text v-if="!canRead" class="pr-8 no-emergency-settings">
      <v-alert color="orange" type="error" prominent class="text-justify">
        {{ $t('settings.emergency.noData')}}
      </v-alert>
    </v-card-text>
    <v-card-text v-else class="pr-8 emergency-caller-id">
      <v-select v-model="profile" class="emergency-callback-profile"
        :disabled="disabledProfile"
        :items="profilesList"
        item-text="name"
        item-value="id"
        :loading="loading"
        :label="$t('settings.emergency.profile')">
        <template v-slot:append-outer>
          <cp-help i18n="emergency.profile"/>
        </template>
      </v-select>
      <Details :data="profileDetails" />
      <v-select v-model="location" class="emergency-dispatchable-location"
        :disabled="disabledLocation"
        item-text="name"
        item-value="id"
        :items="locationsList"
        :loading="loading"
        :label="$t('settings.emergency.location')">
        <template v-slot:append-outer>
          <cp-help i18n="emergency.location"/>
        </template>
      </v-select>
      <Details :data="locationDetails" />
      <v-alert type="warning" dense outlined border="left" class="alert-warn" v-if="showWarn">
        {{ warning }}
      </v-alert>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import Details from './details'
import events from 'Store/events'
import API from '@/api'

export default {
  name: 'settings-emergency',
  data () {
    return {
      loading: false,
      defaultValue: { id: 0, name: this.$t('settings.emergency.default') }
    }
  },
  components: { Details },
  computed: {
    ...mapGetters({
      canProfRead: 'permissions/ecidProfRead',
      canProfWrite: 'permissions/ecidProfWrite',
      canLocRead: 'permissions/ecidLocRead',
      canLocWrite: 'permissions/ecidLocWrite',
      ecidProfReadFor: 'permissions/ecidProfReadFor',
      ecidLocReadFor: 'permissions/ecidLocReadFor',
      ecidProfListFor: 'permissions/ecidProfListFor', // to filter profile the user can see
      ecidLocListFor: 'permissions/ecidLocListFor', // to filter loc the user can see
      eCIDLocations: 'settings/eCIDLocations',
      eCIDProfiles: 'settings/eCIDProfiles',
      profileID: 'settings/eCIDProfileID',
      isOwnerCurUser: 'settings/isOwnerCurUser',
      locationID: 'settings/eCIDLocationID'
    }),
    canRead: function () {
      return this.canProfRead || this.canLocRead || this.ecidProfListFor || this.ecidLocListFor
    },
    canWrite: function () {
      return this.canProfWrite || this.canLocWrite
    },
    disabledProfile: function () {
      return !this.canProfRead || !this.canProfWrite || !this.ecidProfListFor
    },
    disabledLocation: function () {
      return !this.canLocRead || !this.canLocWrite || !this.ecidLocListFor
    },
    showWarn: function () {
      return [this.canProfRead, this.canProfWrite, this.canLocRead, this.canLocWrite, this.ecidProfListFor, this.ecidLocListFor]
        .some(r => !r)
    },
    profilesList: function () {
      return this.makeList(this.eCIDProfiles)
    },
    locationsList: function () {
      return this.makeList(this.eCIDLocations)
    },
    profile: {
      get: function () {
        return this.getSelectValue(this.eCIDProfiles, this.profileID)
      },
      set: function (pid) {
        this.update({ pID: pid, lID: this.locationID })
      }
    },
    location: {
      get: function () {
        return this.getSelectValue(this.eCIDLocations, this.locationID)
      },
      set: function (lid) {
        this.update({ pID: this.profileID, lID: lid })
      }
    },
    profileDetails: function () {
      if (!this.profile.id) {
        return []
      }
      return [{
        name: 'settings.emergency.callerName',
        value: this.profile.caller_id_name
      }, {
        name: 'settings.emergency.callerIDPrefix',
        value: this.profile.caller_id_number
      }, {
        name: 'settings.emergency.callbackNum',
        value: this.profile.callback_number || '-'
      }]
    },
    locationDetails: function () {
      const loc = this.location
      if (!loc.id) {
        return []
      }
      const address = [loc.address_line1, loc.city, loc.state,
        loc.postal_code, loc.country_code]
      return [{
        name: 'settings.emergency.locName',
        value: loc.description
      }, {
        name: 'settings.emergency.address',
        value: address.join(', ')
      }]
    },
    warning: function () {
      if (!this.showWarn) return ''

      const msg = this.$t('settings.emergency.permLimit.title')
      const perms = [
        this.canProfRead ? null : this.$t('settings.emergency.permLimit.profRd'),
        this.canProfWrite ? null : this.$t('settings.emergency.permLimit.profWr'),
        this.canLocRead ? null : this.$t('settings.emergency.permLimit.locRd'),
        this.canLocWrite ? null : this.$t('settings.emergency.permLimit.locWr')
      ]
      return `${msg}: ` + perms.filter(p => p !== null).join(', ')
    },
    lockSettings: function () {
      return !this.isOwnerCurUser
    }
  },
  methods: {
    ...mapActions({
      setProfileID: 'settings/setEmergencyProfileCID',
      setLocationID: 'settings/setEmergencyLocationCID'
    }),
    makeList: function (data) {
      const list = [this.defaultValue]
      data.forEach(p => list.push(p))
      return list
    },
    getSelectValue: function (list, id) {
      const val = list.find(l => l.id === id)
      if (!val) return this.defaultValue
      return val
    },
    update: function (params) {
      this.loading = true
      params.pID = params.pID > 0 ? params.pID : null
      params.lID = params.lID > 0 ? params.lID : null
      API.setEmergencyCID(params.pID, params.lID).then(resp => {
        this.dbg('Emergency caller id setup returns: %o', resp)
        this.setProfileID(resp.data?.emergency_callerid_callback_profile_id)
        this.setLocationID(resp.data?.emergency_callerid_dispatchable_location_id)
      }).catch(err => {
        const msg = API.getException(err) || ''
        events.pubError(`Failed to update emergency settings! ${msg}`)
      }).finally(() => {
        this.loading = false
      })
    }
  }
}
</script>

<style scoped>
</style>
